// src/pages/AuthPage.js
import React, { useState } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";

export default function AuthPage() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") {
        if (!form.name) throw new Error("Name is required");
        await register(form.email, form.password, form.name);
      } else {
        await login(form.email, form.password);
      }
      navigate("/dashboard");
    } catch (err) {
      const msg = err.code === "auth/email-already-in-use" ? "Email already registered"
        : err.code === "auth/invalid-credential" ? "Invalid email or password"
        : err.code === "auth/weak-password" ? "Password must be at least 6 characters"
        : err.message || "Something went wrong";
      setError(msg);
    }
    setLoading(false);
  };

  const features = [
    { icon: "💰", title: "Discounted Rates", desc: "Up to 70% off retail UPS prices" },
    { icon: "⚡", title: "Instant Labels", desc: "Get your shipping label in minutes" },
    { icon: "📦", title: "All UPS Services", desc: "Ground, 2-Day, Next Day & more" },
    { icon: "🛡", title: "Insurance Available", desc: "Protect your shipments" },
    { icon: "💬", title: "Live Support Chat", desc: "Direct communication channel" },
    { icon: "📊", title: "Order Tracking", desc: "Track every shipment in real-time" },
  ];

  return (
    <div className="auth-page">
      <div className="auth-container">
        {/* Left side — features */}
        <div className="auth-features hide-mobile">
          <div style={{ marginBottom: 48 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div className="nav-logo" style={{ width: 48, height: 48, background: "linear-gradient(135deg, #4fc3f7, #7c4dff)" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M2 7l10-5 10 5-10 5L2 7z" fill="rgba(255,255,255,0.9)" /><path d="M2 7v10l10 5V12L2 7z" fill="rgba(255,255,255,0.6)" /><path d="M22 7v10l-10 5V12l10-5z" fill="rgba(255,255,255,0.75)" />
                </svg>
              </div>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30, color: "#e2e8f0", letterSpacing: 2 }}>
                SHIP<span style={{ color: "#4fc3f7" }}>FAST</span>
              </span>
            </div>
            <p style={{ fontSize: 18, color: "#94a3b8", lineHeight: 1.6 }}>
              Ship smarter, not harder. Get discounted UPS labels delivered to your inbox.
            </p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {features.map((f) => (
              <div key={f.title} className="feature-card">
                <span style={{ fontSize: 24 }}>{f.icon}</span>
                <div>
                  <p style={{ fontWeight: 700, color: "#e2e8f0", fontSize: 14 }}>{f.title}</p>
                  <p style={{ color: "#64748b", fontSize: 12, marginTop: 2 }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right side — form */}
        <div className="auth-form-side">
          {/* Mobile logo */}
          <div className="show-mobile" style={{ textAlign: "center", marginBottom: 32 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
              <div className="nav-logo" style={{ width: 48, height: 48, background: "linear-gradient(135deg, #4fc3f7, #7c4dff)" }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M2 7l10-5 10 5-10 5L2 7z" fill="rgba(255,255,255,0.9)" /><path d="M2 7v10l10 5V12L2 7z" fill="rgba(255,255,255,0.6)" /><path d="M22 7v10l-10 5V12l10-5z" fill="rgba(255,255,255,0.75)" />
                </svg>
              </div>
              <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 30, color: "#e2e8f0", letterSpacing: 2 }}>
                SHIP<span style={{ color: "#4fc3f7" }}>FAST</span>
              </span>
            </div>
          </div>

          <div className="card" style={{ maxWidth: 420, width: "100%", margin: "0 auto" }}>
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h2>
            <p style={{ color: "#64748b", fontSize: 14, marginBottom: 28 }}>
              {mode === "login" ? "Sign in to manage your shipments" : "Start shipping with discounted rates"}
            </p>

            {/* Tabs */}
            <div style={{ display: "flex", marginBottom: 28, borderRadius: 10, overflow: "hidden", border: "1px solid rgba(255,255,255,0.06)" }}>
              {["login", "register"].map((m) => (
                <button key={m} onClick={() => { setMode(m); setError(""); }}
                  className={`auth-tab ${mode === m ? "auth-tab-active" : ""}`}>
                  {m === "login" ? "Sign In" : "Create Account"}
                </button>
              ))}
            </div>

            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 18 }}>
              {mode === "register" && (
                <div className="animate-slide-down">
                  <label className="sf-label">Full Name</label>
                  <input className="sf-input" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" />
                </div>
              )}
              <div>
                <label className="sf-label">Email</label>
                <input className="sf-input" type="email" value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" required />
              </div>
              <div>
                <label className="sf-label">Password</label>
                <input className="sf-input" type="password" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" required />
              </div>

              {error && (
                <div className="error-banner animate-shake">
                  <span>⚠</span> {error}
                </div>
              )}

              <button type="submit" className="btn btn-primary btn-glow"
                style={{ width: "100%", justifyContent: "center", padding: "14px 0", fontSize: 16 }}
                disabled={loading}>
                {loading ? (
                  <span className="spinner" />
                ) : (
                  mode === "login" ? "Sign In" : "Create Account"
                )}
              </button>
            </form>

            <p style={{ textAlign: "center", fontSize: 12, color: "#475569", marginTop: 20 }}>
              Secure, encrypted connection
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
